import { useState, useEffect } from 'react';
import { 
  Lock, Sliders, Save, RefreshCw, Layers, ShieldAlert,
  Database, Fingerprint, ChevronRight
} from 'lucide-react';

const CATEGORIES = [
  { id: 'general', label: 'Hệ thống Chung', icon: Sliders },
  { id: 'decree278', label: 'Nghị định 278', icon: ShieldAlert },
  { id: 'data2024', label: 'Luật Dữ liệu 2024', icon: Database },
  { id: 'identity', label: 'Quản lý Định danh', icon: Fingerprint },
];

const MOCK_RULES = [
  { id: 'R-001', cat: 'decree278', title: 'Mã hoá AES-256 Nhóm 1', desc: 'Bắt buộc toàn bộ dữ liệu lưu trữ tĩnh (Data at Rest) của Nhóm 1 phải được mã hoá bằng thuật toán quốc gia.', active: true },
  { id: 'R-002', cat: 'decree278', title: 'Tự động ngắt TLS Certificate', desc: 'Gateway sẽ drop request nếu chứng chỉ số của Node trạm hết hạn quá 24h.', active: true },
  { id: 'R-003', cat: 'data2024', title: 'Audit Hash-Chain Toàn vẹn', desc: 'Xác minh Blockchain hash định kỳ đối với sổ cái logs giao dịch hệ thống.', active: false },
  { id: 'R-004', cat: 'general', title: 'Email Báo cáo Tự động 00:00', desc: 'Cronjob trigger tiến trình gửi kết quả đánh giá ngày cho bộ phận Pháp chế.', active: true },
  { id: 'R-005', cat: 'identity', title: 'Khoá tài khoản (Ban IP)', desc: 'Keycloak Policy áp dụng ban IP/User Account tạm thời sau 5 lần sai mật khẩu.', active: true },
];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('decree278');
  const [rules, setRules] = useState(MOCK_RULES);

  useEffect(() => {
    console.log('SettingsPage component mounted');
  }, []);

  const toggleRule = (id: string, current: boolean) => {
    setRules(rules.map(r => r.id === id ? { ...r, active: !current } : r));
  };

  const filteredRules = rules.filter(r => r.cat === activeTab);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative pb-10 flex flex-col h-full min-h-[70vh]">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 shrink-0">
         <div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3 leading-tight">
              <Lock className="text-indigo-600 dark:text-indigo-400" size={32} />
              Cấu hình Quy tắc
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mt-2 max-w-2xl text-[11px] font-black tracking-tight leading-loose">
              Thiết lập các tiêu chí kiểm tra tuân thủ dữ liệu. Mọi thay đổi sẽ được áp dụng trực tiếp vào Engine quét tự động Core SOC.
            </p>
         </div>
         <div className="flex gap-3">
            <button className="px-6 py-2.5 rounded-xl text-[10px] font-black text-slate-700 dark:text-slate-400 border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 transition-all flex items-center gap-2 tracking-widest shadow-sm active:scale-95">
               <RefreshCw size={14} /> Hoàn tác
            </button>
            <button className="px-8 py-2.5 rounded-xl text-[10px] font-black bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-[0_10px_30px_rgba(99,102,241,0.3)] hover:shadow-[0_15px_40px_rgba(99,102,241,0.5)] transition-all flex items-center gap-2 tracking-[0.2em] transform hover:-translate-y-0.5 active:scale-95">
               <Save size={14} /> Lưu Cấu hình
            </button>
         </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
         {/* SIDEBAR TABS */}
         <div className="md:col-span-3 space-y-2">
            <h3 className="text-[10px] font-black text-slate-500 dark:text-slate-500 tracking-[0.3em] mb-4 pl-3">Phân loại Engine</h3>
            {CATEGORIES.map(cat => (
               <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl text-[11px] font-black transition-all border tracking-widest ${
                     activeTab === cat.id 
                       ? 'bg-indigo-600 text-white border-indigo-400 shadow-lg shadow-indigo-500/20' 
                       : 'bg-white/50 dark:bg-black/20 text-slate-600 dark:text-slate-400 border-slate-100 dark:border-white/5 hover:bg-slate-100 dark:hover:bg-white/10 active:scale-95'
                  }`}
               >
                  <cat.icon size={18} className={activeTab === cat.id ? 'text-white' : 'text-slate-500 dark:text-slate-600'} />
                  {cat.label}
               </button>
            ))}
         </div>

         {/* RULES LIST */}
         <div className="md:col-span-9 glass-panel border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] rounded-[2.5rem] p-8 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/[0.02] blur-[110px] rounded-full" />
            
            <div className="flex items-center justify-between mb-10 pb-6 border-b border-slate-100 dark:border-white/5 relative z-10">
               <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-inner">
                     <Layers size={22} />
                  </div>
                  <div>
                    <h3 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-[0.2em]">Cấu hình quy tắc</h3>
                    <p className="text-lg font-black text-slate-900 dark:text-white tracking-tight mt-0.5">
                       {CATEGORIES.find(c => c.id === activeTab)?.label}
                    </p>
                  </div>
               </div>
               <span className="text-[10px] bg-slate-100 dark:bg-white/10 text-slate-900 dark:text-white font-black px-4 py-1.5 rounded-full border border-slate-200 dark:border-white/10 tracking-widest shadow-sm">
                  {filteredRules.length} Quy tắc / Logic
               </span>
            </div>

            <div className="space-y-5 relative z-10">
               {filteredRules.map((rule) => (
                  <div
                     key={rule.id}
                     className={`p-6 rounded-[2rem] border transition-all flex items-start gap-6 group relative shadow-inner ${
                        rule.active 
                          ? 'bg-slate-50 dark:bg-white/5 border-indigo-500/20 dark:border-indigo-500/20 hover:border-indigo-500/40' 
                          : 'bg-white/40 dark:bg-black/40 border-slate-100 dark:border-white/5 opacity-60'
                     }`}
                  >
                     <div className="pt-1 select-none">
                        <div 
                           className={`w-12 h-6.5 rounded-full relative cursor-pointer transition-all border duration-500 ${
                              rule.active ? 'bg-indigo-600 border-indigo-400' : 'bg-slate-300 dark:bg-slate-700 border-slate-200 dark:border-slate-800'
                           }`}
                           onClick={() => toggleRule(rule.id, rule.active)}
                        >
                           <div 
                              className={`absolute top-[3px] left-[3px] w-4.5 h-4.5 bg-white rounded-full transition-all duration-500 shadow-lg ${
                                 rule.active ? 'translate-x-[22px]' : 'translate-x-0'
                              }`} 
                           />
                        </div>
                     </div>
                     
                     <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-4 mb-2">
                           <span className="text-[9px] font-mono font-black text-slate-500 dark:text-slate-500 bg-white dark:bg-black/40 px-2.5 py-1 rounded-lg border border-slate-100 dark:border-white/10 tracking-tighter shadow-sm">
                              {rule.id}
                           </span>
                           <h4 className={`text-base font-black tracking-tight ${rule.active ? 'text-slate-950 dark:text-white' : 'text-slate-500 dark:text-slate-500'}`}>
                              {rule.title}
                           </h4>
                        </div>
                        <p className={`text-xs font-medium leading-relaxed max-w-2xl ${rule.active ? 'text-slate-700 dark:text-slate-400' : 'text-slate-500 dark:text-slate-600'}`}>
                           {rule.desc}
                        </p>
                     </div>

                     <button className="p-2.5 text-slate-300 dark:text-slate-700 hover:text-indigo-600 dark:hover:text-white hover:bg-white dark:hover:bg-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-all shadow-sm active:scale-95">
                        <ChevronRight size={18} />
                     </button>
                  </div>
               ))}

               {filteredRules.length === 0 && (
                  <div className="py-24 text-center">
                     <div className="w-16 h-16 bg-slate-50 dark:bg-white/5 rounded-full flex items-center justify-center text-slate-200 dark:text-slate-800 mx-auto mb-4 border border-slate-100 dark:border-white/5">
                        <Sliders size={32} />
                     </div>
                     <p className="text-[10px] font-black text-slate-500 dark:text-slate-500 tracking-widest italic">Hệ thống chưa ghi nhận quy tắc cho nhóm này</p>
                  </div>
               )}
            </div>
         </div>
      </div>
    </div>
  );
}
